import * as sensorGroupActions from '@Actions/sensorGroup.action';

import {
	CreateUpdateSensorGroupFormValues,
	ListSensorGroupsParams,
	PaginatedSensorGroupPairs,
	PaginatedSensorGroups,
	SensorGroupConfig,
	SensorGroupPair,
	SensorGroupSensor,
} from '@Types/sensorGroup.types';
import {
	createSensorGroupPairService,
	deleteSensorGroupPairService,
	deleteSensorGroupService,
	getSensorGroupPairService,
	getSensorGroupsService,
	getSingleSensorGroup,
	upsertSensorGroupService,
} from '@Services/sensorGroups';
import { error, success } from '@innovyze/stylovyze';
import { put, retry, takeLatest } from 'redux-saga/effects';

import { AxiosResponse } from 'axios';
import { PayloadAction } from '@reduxjs/toolkit';
import { addRouteNamespace } from '@Utils/actions';
import { navigateTo } from '@Actions/navigate.action';
import { upsertSensorGroupMessages } from '@Utils/sensorGroups.constant';
import { ConfiguredSensor, SensorsResponse } from '@Types';
import { getSensors } from '@Services/sensors';
import { MAIN_ROUTES } from '@Types/routes';

function* getSensorGroupsSaga(action: PayloadAction<ListSensorGroupsParams>) {
	const { getSensorGroupsResolved, getSensorGroupsRejected } =
		sensorGroupActions;
	try {
		const response: AxiosResponse<PaginatedSensorGroups> = yield retry(
			3,
			1500,
			getSensorGroupsService,
			action.payload,
		);
		if (response.data) {
			yield put(getSensorGroupsResolved(response.data));
		} else {
			console.error(
				`SensorGroup service returned with empty response: ${response}`,
			);
			yield put(getSensorGroupsRejected());
			yield put(error('Failed to get sensor groups'));
		}
	} catch (e) {
		console.error(`Failed to call SensorGroup service: ${e}`);
		yield put(getSensorGroupsRejected());
		yield put(error('Failed to get sensor groups'));
	}
}
function* getSingleSensorGroupSaga(action: PayloadAction<string>) {
	const {
		getSingleSensorGroupByIdResolved,
		getSingleSensorGroupByIdRejected,
	} = sensorGroupActions;
	try {
		const response: AxiosResponse<SensorGroupConfig> = yield retry(
			3,
			1500,
			getSingleSensorGroup,
			action.payload,
		);
		if (response.data) {
			yield put(getSingleSensorGroupByIdResolved(response.data));
			yield put(sensorGroupActions.getSensorGroupPairs(action.payload));
		} else {
			yield put(getSingleSensorGroupByIdRejected());
			yield put(error('Failed to get sensor group'));
		}
	} catch (e) {
		console.error(e);
		yield put(getSingleSensorGroupByIdRejected());
		yield put(error('Failed to get sensor group'));
	}
}

function* getSensorsSaga(
	action: PayloadAction<{
		type: 'virtual' | 'physical' | undefined;
		sensorType: string;
	}>,
) {
	const { getSensorsResolved, getSensorsRejected } = sensorGroupActions;
	try {
		const { data }: AxiosResponse<SensorsResponse> = yield retry(
			5,
			1500,
			getSensors,
			action.payload.type,
			action.payload.sensorType,
		);
		const sensors: ConfiguredSensor[] = data.sensors?.map(
			({
				sensorId,
				sensorAlias,
				sensorType,
				sensorUnit,
				channelType,
				assetName,
				resolutions,
			}) => ({
				sensorId,
				sensorAlias,
				sensorType,
				unit: sensorUnit,
				channelType,
				assetName,
				resolutions,
			}),
		);
		yield put(getSensorsResolved(sensors));
	} catch (ex) {
		yield put(getSensorsRejected());
		yield put(error('sensorGroup/getSensors/error'));
	}
}

function* upsertSensorGroupConfigSaga(
	action: PayloadAction<CreateUpdateSensorGroupFormValues>,
) {
	const { upsertSensorGroupConfigRejected, upsertSensorGroupConfigResolved } =
		sensorGroupActions;
	try {
		const { _id } = action.payload;
		const isUpdate = !!_id;
		const mode = isUpdate ? 'update' : 'create';
		const response: AxiosResponse<{ _id: string }> = yield retry(
			1,
			10000,
			upsertSensorGroupService,
			action.payload,
		);
		if (response.data) {
			if (action.payload.sensors && response.data._id) {
				const { originalSensors, sensors: newSensors } = action.payload;

				const sensorsToCreate: SensorGroupSensor[] = [];
				for (const newSensor of newSensors) {
					if (
						!originalSensors?.find(
							sensor =>
								sensor.sensorId == newSensor.sensorId &&
								sensor.channelType == newSensor.channelType,
						)
					) {
						sensorsToCreate.push(newSensor);
					}
				}
				const sensorsToDelete: SensorGroupSensor[] = [];
				if (originalSensors) {
					for (const originalSensor of originalSensors) {
						if (
							!newSensors?.find(
								sensor =>
									sensor.sensorId ==
										originalSensor.sensorId &&
									sensor.channelType ==
										originalSensor.channelType,
							)
						) {
							sensorsToDelete.push(originalSensor);
						}
					}
				}

				const createResponse: 'success' | 'error' = yield retry(
					5,
					10000,
					createSensorGroupPairService,
					{
						sensorGroupConfig: response.data._id,
						sensors: sensorsToCreate,
						verb: mode,
					},
				);
				const deleteResponse: 'success' | 'error' = yield retry(
					5,
					10000,
					deleteSensorGroupPairService,
					{
						sensorGroupConfig: response.data._id,
						sensors: sensorsToDelete,
						verb: mode,
					},
				);
				if (
					createResponse === 'success' &&
					deleteResponse === 'success'
				) {
					yield put(
						success(
							// eslint-disable-next-line prettier/prettier
							upsertSensorGroupMessages[`sensorGroup/${mode}/success`],
						),
					);
					yield put(upsertSensorGroupConfigResolved());
					yield put(navigateTo(MAIN_ROUTES.SensorGroups));
				} else {
					yield put(
						error(
							// eslint-disable-next-line prettier/prettier
							upsertSensorGroupMessages[`sensorGroup/${mode}/error`],
						),
					);
					yield put(upsertSensorGroupConfigRejected());
				}
			} else {
				yield put(
					error(
						// eslint-disable-next-line prettier/prettier
						upsertSensorGroupMessages[`sensorGroup/${mode}/duplicate`],
					),
				);
				yield put(upsertSensorGroupConfigRejected());
			}
		} else {
			yield put(
				error(upsertSensorGroupMessages[`sensorGroup/${mode}/error`]),
			);
			yield put(upsertSensorGroupConfigRejected());
		}
	} catch (e) {
		console.error(e);
		yield put(error(upsertSensorGroupMessages['sensorGroup/upsert/error']));
		yield put(upsertSensorGroupConfigRejected());
	}
}

function* getSensorGroupPairsSaga(action: PayloadAction<string>) {
	const { getSensorGroupPairsResolved, getSensorGroupPairsRejected } =
		sensorGroupActions;
	try {
		const response: AxiosResponse<PaginatedSensorGroupPairs> = yield retry(
			3,
			1500,
			getSensorGroupPairService,
			action.payload,
		);
		if (response.data) {
			yield put(getSensorGroupPairsResolved(response.data));
		} else {
			yield put(getSensorGroupPairsRejected());
			yield put(error('Failed to get sensors from sensor group'));
		}
	} catch (e) {
		console.error(e);
		yield put(getSensorGroupPairsRejected());
		yield put(error('Failed to get sensors from sensor group'));
	}
}

function* deleteSensorGroupSaga(action: PayloadAction<string>) {
	const { deleteSensorGroupResolved, deleteSensorGroupRejected } =
		sensorGroupActions;
	try {
		const sensorConfigPairResponse: AxiosResponse<{
			sensorConfigPairs: SensorGroupPair[];
		}> = yield retry(3, 1500, deleteSensorGroupService, action.payload);
		if (sensorConfigPairResponse.data) {
			yield put(deleteSensorGroupResolved());
			yield put(success('Sensor group deleted successfully'));
			yield put(navigateTo(addRouteNamespace('sensor-groups')));
		} else {
			yield put(deleteSensorGroupRejected());
			yield put(error('Failed to delete sensor group'));
		}
	} catch (e) {
		console.error(e);
		yield put(deleteSensorGroupRejected());
		yield put(error('Failed to delete sensor group'));
	}
}

function* getSensorGroupMapSaga(action: PayloadAction<ListSensorGroupsParams>) {
	const { getSensorGroupMapResolved, getSensorGroupMapRejected } =
		sensorGroupActions;
	try {
		const payload: AxiosResponse<PaginatedSensorGroups> = yield retry(
			5,
			1500,
			getSensorGroupsService,
			action.payload,
		);
		if (payload.data) {
			const sensorGroupMap: { [key: string]: SensorGroupConfig } = {};
			payload.data.sensorGroups.forEach(configuration => {
				sensorGroupMap[`${configuration._id}`] = configuration;
			});
			yield put(getSensorGroupMapResolved(sensorGroupMap));
		} else {
			throw new Error('OperationStatus != SUCCESS');
		}
	} catch (e) {
		yield put(getSensorGroupMapRejected());
	}
}

function* watchGetSensorGroupMap() {
	yield takeLatest(
		sensorGroupActions.getSensorGroupMap,
		getSensorGroupMapSaga,
	);
}

function* watchGetSensorGroups() {
	yield takeLatest(sensorGroupActions.getSensorGroups, getSensorGroupsSaga);
}
function* watchGetSingleSensorGroup() {
	yield takeLatest(
		sensorGroupActions.getSingleSensorGroupById,
		getSingleSensorGroupSaga,
	);
}
function* watchUpsertSensorGroup() {
	yield takeLatest(
		sensorGroupActions.upsertSensorGroupConfig,
		upsertSensorGroupConfigSaga,
	);
}
function* watchDeleteSensorGroup() {
	yield takeLatest(
		sensorGroupActions.deleteSensorGroup,
		deleteSensorGroupSaga,
	);
}
function* watchGetSensorGroupPairs() {
	yield takeLatest(
		sensorGroupActions.getSensorGroupPairs,
		getSensorGroupPairsSaga,
	);
}
function* watchGetSensors() {
	yield takeLatest(sensorGroupActions.getSensors, getSensorsSaga);
}

export default [
	watchGetSensors(),
	watchGetSensorGroups(),
	watchGetSingleSensorGroup(),
	watchUpsertSensorGroup(),
	watchDeleteSensorGroup(),
	watchGetSensorGroupPairs(),
	watchGetSensorGroupMap(),
];
